import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { Banner } from '../../types';
import { View } from '../../types';

const BANNER_DISMISSED_KEY = 'eduapp_banner_dismissed';

const GUEST_KEY = 'guest';

function getDismissedIds(userId: string | null): Record<string, string> {
  const key = userId || GUEST_KEY;
  try {
    const raw = localStorage.getItem(`${BANNER_DISMISSED_KEY}_${key}`);
    return raw ? JSON.parse(raw) : {};
  } catch {
    return {};
  }
}

function persistDismiss(userId: string | null, bannerId: number, dismissMode: string) {
  const key = userId || GUEST_KEY;
  try {
    const current = getDismissedIds(userId);
    const k = dismissMode === 'forever' ? `f_${bannerId}` : `d_${bannerId}`;
    const value = dismissMode === 'day' ? new Date().toDateString() : '1';
    current[k] = value;
    localStorage.setItem(`${BANNER_DISMISSED_KEY}_${key}`, JSON.stringify(current));
  } catch {}
}

function isDismissed(banner: Banner, userId: string | null, dismissed: Record<string, string>, sessionDismissed: Set<number>): boolean {
  if (banner.dismissMode === 'session') return sessionDismissed.has(banner.id);
  const key = banner.dismissMode === 'forever' ? `f_${banner.id}` : `d_${banner.id}`;
  const stored = dismissed[key];
  if (!stored) return false;
  if (banner.dismissMode === 'day') return stored === new Date().toDateString();
  return true;
}

interface BannerPopupProps {
  banners: Banner[];
  userId: string | null;
  onNavigate?: (view: View, payload?: object) => void;
}

const BannerPopup: React.FC<BannerPopupProps> = ({ banners, userId, onNavigate }) => {
  const [dismissed, setDismissedState] = useState<Record<string, string>>({});
  const [sessionDismissed, setSessionDismissed] = useState<Set<number>>(new Set());
  const [currentIndex, setCurrentIndex] = useState(0);

  useEffect(() => {
    setDismissedState(getDismissedIds(userId));
  }, [userId]);

  const visibleBanners = useMemo(() => {
    return banners.filter(b => !isDismissed(b, userId, dismissed, sessionDismissed));
  }, [banners, userId, dismissed, sessionDismissed]);

  const visibleBanner = visibleBanners[currentIndex] || null;

  const handleDismiss = useCallback(() => {
    if (!visibleBanner) return;
    if (visibleBanner.dismissMode === 'session') {
      setSessionDismissed(prev => new Set(prev).add(visibleBanner.id));
    } else {
      persistDismiss(userId, visibleBanner.id, visibleBanner.dismissMode);
      setDismissedState(getDismissedIds(userId));
    }
    setCurrentIndex(0);
  }, [visibleBanner, userId]);

  const handleLinkClick = useCallback(() => {
    if (!visibleBanner) return;
    if (visibleBanner.linkPayload && onNavigate) {
      onNavigate(visibleBanner.linkPayload.view, visibleBanner.linkPayload.payload);
    } else if (visibleBanner.linkUrl) {
      window.open(visibleBanner.linkUrl, '_blank');
    }
    handleDismiss();
  }, [visibleBanner, onNavigate, handleDismiss]);

  if (!visibleBanner) return null;

  const typeStyles: Record<string, string> = {
    promo: 'from-amber-500/20 to-orange-500/10 border-amber-200',
    new_feature: 'from-teal-500/20 to-emerald-500/10 border-teal-200',
    info: 'from-blue-500/20 to-indigo-500/10 border-blue-200',
    maintenance: 'from-slate-500/20 to-slate-600/10 border-slate-300',
  };
  const style = typeStyles[visibleBanner.type] || typeStyles.info;

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-black/40 backdrop-blur-sm">
      <div className={`relative max-w-lg w-full rounded-2xl border-2 bg-gradient-to-br ${style} shadow-2xl overflow-hidden`}>
        <button
          onClick={handleDismiss}
          className="absolute top-4 right-4 w-8 h-8 rounded-full bg-white/20 hover:bg-white/40 flex items-center justify-center text-slate-600 transition-colors"
          aria-label="Đóng"
        >
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
          </svg>
        </button>
        <div className="p-8 pt-12">
          <h3 className="text-xl font-black text-slate-800 mb-3">{visibleBanner.title}</h3>
          {(visibleBanner.mediaType === 'video' && visibleBanner.videoUrl) ? (
            <div className="mb-6 rounded-xl overflow-hidden bg-black">
              {(() => {
                const url = visibleBanner.videoUrl!;
                const ytMatch = url.match(/(?:youtube\.com\/watch\?v=|youtu\.be\/)([^&?/]+)/);
                if (ytMatch) {
                  return (
                    <iframe
                      src={`https://www.youtube.com/embed/${ytMatch[1]}`}
                      title={visibleBanner.title}
                      className="w-full aspect-video"
                      allowFullScreen
                    />
                  );
                }
                const vimeoMatch = url.match(/vimeo\.com\/(?:video\/)?(\d+)/);
                if (vimeoMatch) {
                  return (
                    <iframe
                      src={`https://player.vimeo.com/video/${vimeoMatch[1]}`}
                      title={visibleBanner.title}
                      className="w-full aspect-video"
                      allowFullScreen
                    />
                  );
                }
                return <video src={url} controls className="w-full max-h-64" playsInline />;
              })()}
              {visibleBanner.content && (
                <div className="text-sm text-slate-700 p-3" dangerouslySetInnerHTML={{ __html: visibleBanner.content }} />
              )}
            </div>
          ) : (visibleBanner.mediaType === 'image' && visibleBanner.imageUrl) ? (
            <div className="mb-6 rounded-xl overflow-hidden">
              <img src={visibleBanner.imageUrl} alt={visibleBanner.title} className="w-full h-auto max-h-72 object-contain" />
              {visibleBanner.content && (
                <div className="text-sm text-slate-700 mt-3 prose prose-sm max-w-none" dangerouslySetInnerHTML={{ __html: visibleBanner.content }} />
              )}
            </div>
          ) : (
            <div 
              className="text-sm text-slate-700 leading-relaxed mb-6 prose prose-sm max-w-none"
              dangerouslySetInnerHTML={{ __html: visibleBanner.content || '' }}
            />
          )}
          <div className="flex gap-3">
            {(visibleBanner.linkUrl || visibleBanner.linkPayload) && (
              <button
                onClick={handleLinkClick}
                className="px-6 py-2.5 bg-primary-500 hover:bg-primary-600 text-white text-sm font-bold rounded-xl transition-colors"
              >
                {visibleBanner.linkText || 'Xem thêm'}
              </button>
            )}
            <button
              onClick={handleDismiss}
              className="px-6 py-2.5 bg-slate-200 hover:bg-slate-300 text-slate-700 text-sm font-bold rounded-xl transition-colors"
            >
              Đóng
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default BannerPopup;
